'use client';

import { css } from 'styled-components';

import type { ButtonVariant } from '../packages/button';
import type { ColorsTypeOld, ColorsType } from '../types/colors';

type ButtonTheme = {
  [variantKey in ButtonVariant]: ColorPalette;
};

type ColorPalette = {
  [colorKey in ColorScheme]: {
    text: string;
    background?: string;
    border?: string;
    icon?: string;
  };
};

export type ColorScheme = 'primary' | 'default' | 'secondary';

export const buttonTheme: ButtonTheme = {
  contained: {
    default: {
      text: '--button--contained--default-text',
      background: '--button--contained--default-background',
      border: '--button--contained--default-border',
      icon: '--button--contained--default-icon',
    },
    secondary: {
      text: '--button--contained--secondary-text',
      background: '--button--contained--secondary-background',
      border: '--button--contained--secondary-border',
    },
    primary: {
      text: '--button--contained--primary-text',
      background: '--button--contained--primary-background',
      border: '--button--contained--primary-border',
    },
  },

  outline: {
    default: {
      text: '--button--outline--default-text',
      border: '--button--outline--default-border',
      icon: '--button--outline--default-icon',
    },
    secondary: {
      text: '--button--outline--secondary-text',
      border: '--button--outline--secondary-border',
    },
    primary: {
      text: '--button--outline--primary-text',
      border: '--button--outline--primary-border',
    },
  },

  text: {
    default: {
      text: '--button--text--default-text',
    },
    secondary: {
      text: '--button--text--secondary-text',
    },
    primary: {
      text: '--button--text--primary-text',
    },
  },
};

export const buttonBehaviorTheme = {
  contained: {
    textDisable: '--button--contained--text-disable',
    backgroundDisable: '--button--contained--background-disable',
    spinner: '--button--contained--spinner',
    spinnerBorder: '--button--contained--spinner-border',
  },
  outline: {
    textDisable: '--button--outline--text-disable',
    backgroundDisable: '--button--outline--background-disable',
    spinner: '--button--outline--spinner',
    spinnerBorder: '--button--outline--spinner-border',
  },
};

type Args =
  | {
      Colors: ColorsType;
      hasNewColorTheming: true;
    }
  | {
      Colors: ColorsTypeOld;
      hasNewColorTheming: false;
    };

export function buttonLight(args: Args) {
  const { Colors, hasNewColorTheming } = args;
  return css`
    ${buttonTheme.contained.default.text}: ${hasNewColorTheming ? Colors.buttonText : Colors.white};
    ${buttonTheme.contained.default.background}: ${hasNewColorTheming ? Colors.buttonBg : Colors.black3};
    ${buttonTheme.contained.default.border}: ${hasNewColorTheming ? Colors.buttonBorder : Colors.black3};
    ${buttonTheme.contained.default.icon}: ${hasNewColorTheming ? Colors.buttonIcon : Colors.primary1};

    ${buttonTheme.outline.default.text}: ${Colors.text};
    ${buttonTheme.outline.default.border}: ${Colors.grey6};
    ${buttonTheme.outline.default.icon}: ${hasNewColorTheming ? Colors.buttonIcon : Colors.primary2};

    ${buttonTheme.text.default.text}: ${Colors.text};

    ${buttonTheme.contained.secondary.text}: ${Colors.text};
    ${buttonTheme.contained.secondary.background}: ${Colors.white};
    ${buttonTheme.contained.secondary.border}: ${Colors.white};
    ${buttonTheme.outline.secondary.text}: ${Colors.text};
    ${buttonTheme.outline.secondary.border}: ${Colors.grey6};
    ${buttonTheme.text.secondary.text}: ${Colors.white};

    ${buttonTheme.contained.primary.text}: ${Colors.white};
    ${buttonTheme.contained.primary.background}: ${Colors.primary1};
    ${buttonTheme.contained.primary.border}: ${Colors.primary1};
    ${buttonTheme.outline.primary.text}: ${Colors.text};
    ${buttonTheme.outline.primary.border}: ${Colors.primary1};
    ${buttonTheme.text.primary.text}: ${Colors.text};
  `;
}

export function buttonDark(args: Args) {
  const { Colors, hasNewColorTheming } = args;
  return css`
    ${buttonTheme.contained.default.text}: ${hasNewColorTheming ? Colors.buttonText : Colors.text};
    ${buttonTheme.contained.default.background}: ${hasNewColorTheming ? Colors.buttonBg : Colors.grey7};
    ${buttonTheme.contained.default.border}: ${hasNewColorTheming ? Colors.buttonBorder : Colors.grey7};
    ${buttonTheme.contained.default.icon}: ${hasNewColorTheming ? Colors.buttonIcon : Colors.primary1};

    ${buttonTheme.outline.default.text}: ${Colors.text};
    ${buttonTheme.outline.default.border}: ${Colors.black5};
    ${buttonTheme.outline.default.icon}: ${hasNewColorTheming ? Colors.buttonIcon : Colors.primary2};

    ${buttonTheme.text.default.text}: ${Colors.text};

    ${buttonTheme.contained.secondary.text}: ${Colors.text};
    ${buttonTheme.contained.secondary.background}: ${Colors.grey7};
    ${buttonTheme.contained.secondary.border}: ${Colors.grey7};
    ${buttonTheme.outline.secondary.text}: ${Colors.text};
    ${buttonTheme.outline.secondary.border}: ${Colors.black5};
    ${buttonTheme.text.secondary.text}: ${Colors.text};

    ${buttonTheme.contained.primary.text}: ${Colors.text};
    ${buttonTheme.contained.primary.background}: ${Colors.primary1};
    ${buttonTheme.contained.primary.border}: ${Colors.primary1};
    ${buttonTheme.outline.primary.text}: ${Colors.text};
    ${buttonTheme.outline.primary.border}: ${Colors.primary1};
    ${buttonTheme.text.primary.text}: ${Colors.text};
  `;
}

export const buttonBehaviorLight = (Colors: ColorsTypeOld | ColorsType) => css`
  ${buttonBehaviorTheme.contained.textDisable}: ${Colors.grey3};
  ${buttonBehaviorTheme.contained.backgroundDisable}: ${Colors.grey1};
  ${buttonBehaviorTheme.contained.spinner}: ${Colors.white};
  ${buttonBehaviorTheme.contained.spinnerBorder}: ${Colors.grey3};

  ${buttonBehaviorTheme.outline.textDisable}: ${Colors.grey3};
  ${buttonBehaviorTheme.outline.backgroundDisable}: 'transparent';
  ${buttonBehaviorTheme.outline.spinner}: ${Colors.black3};
  ${buttonBehaviorTheme.outline.spinnerBorder}: ${Colors.grey3};
`;

export const buttonBehaviorDark = (Colors: ColorsTypeOld | ColorsType) => css`
  ${buttonBehaviorTheme.contained.textDisable}: ${Colors.grey3};
  ${buttonBehaviorTheme.contained.backgroundDisable}: ${Colors.grey7};
  ${buttonBehaviorTheme.contained.spinner}: ${Colors.white};
  ${buttonBehaviorTheme.contained.spinnerBorder}: ${Colors.grey3};

  ${buttonBehaviorTheme.outline.textDisable}: ${Colors.grey3};
  ${buttonBehaviorTheme.outline.backgroundDisable}: 'transparent';
  ${buttonBehaviorTheme.outline.spinner}: ${Colors.black3};
  ${buttonBehaviorTheme.outline.spinnerBorder}: ${Colors.grey7};
`;
